import { Component, computed, DestroyRef, effect, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { RatingScalePresentation } from '@core/candidate-evaluation-settings-types';
import { TranslatePipe } from '@core/i18n-legacy';
import { IconComponent, InputComponent } from '@core/user-interface-legacy';
import { RatingScaleMode } from '@recruitee/candidate-evaluation-settings-types';
import { RatingScalePresentationService } from '@recruitee/candidate-evaluation-settings-util';

@Component({
  selector: 'rt-rating-scale',
  templateUrl: './rating-scale.component.html',
  styleUrl: './rating-scale.component.less',
  imports: [ReactiveFormsModule, TranslatePipe, IconComponent, InputComponent],
})
export class RatingScaleComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly presentationService = inject(RatingScalePresentationService);

  protected readonly minLabelControl = new FormControl('', { nonNullable: true });
  protected readonly maxLabelControl = new FormControl('', { nonNullable: true });

  protected readonly RatingScaleMode = RatingScaleMode;

  protected readonly items = computed(
    () => this.presentationService.getConfig(this.presentation(), this.range()) ?? [],
  );
  protected readonly showLabelsSection = computed(() => {
    if (this.mode() === RatingScaleMode.Edit) return this.editableMinMaxLabels();
    return this.minLabel() || this.maxLabel();
  });

  public readonly mode = input<RatingScaleMode>(RatingScaleMode.Select);
  public readonly presentation = input.required<RatingScalePresentation>();
  public readonly range = input.required<number>();
  public readonly score = input<number | null>(null);
  public readonly editableMinMaxLabels = input<boolean>(false);
  public readonly minLabel = input<string | null>(null);
  public readonly maxLabel = input<string | null>(null);

  public readonly scoreChange = output<number>();
  public readonly minLabelChange = output<string>();
  public readonly maxLabelChange = output<string>();

  private _minLabelChange = this.minLabelControl.valueChanges
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(label => this.minLabelChange.emit(label));
  private _maxLabelChange = this.maxLabelControl.valueChanges
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(label => this.maxLabelChange.emit(label));

  private _syncMinLabel = effect(() => this.syncControl(this.minLabelControl, this.minLabel()));
  private _syncMaxLabel = effect(() => this.syncControl(this.maxLabelControl, this.maxLabel()));

  protected select(score: number): void {
    this.scoreChange.emit(score);
  }

  private syncControl(control: FormControl<string>, label: string | null): void {
    const value = label ?? '';

    if (value !== control.value) {
      control.setValue(value, { emitEvent: false });
    }
  }
}
